import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Ban, Store, Percent, FileText, Globe } from "lucide-react";

const AdminSellerDetail = () => {
  const { id } = useParams();
  const { lang } = useI18n();
  const [s, setS] = useState(null);
  const [reason, setReason] = useState("");
  const [commission, setCommission] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => apiClient.get(`/admin/sellers/${id}`).then((r) => {
    setS(r.data);
    setCommission(r.data.commission_pct ?? "");
  });
  useEffect(() => { load(); }, [id]);

  if (!s) return <div className="text-ink-muted">Loading…</div>;

  const updateStatus = async (status) => {
    if ((status === "rejected" || status === "suspended") && !reason.trim()) {
      toast.error(lang === "nl" ? "Geef een reden" : "Reason required");
      return;
    }
    setBusy(true);
    try {
      const payload = { status, reason };
      if (commission !== "") payload.commission_pct = parseFloat(commission);
      await apiClient.put(`/admin/sellers/${id}/status`, payload);
      toast.success(lang === "nl" ? "Status bijgewerkt" : "Status updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveCommission = async () => {
    try {
      await apiClient.put(`/admin/sellers/${id}/commission`, { commission_pct: commission === "" ? null : parseFloat(commission) });
      toast.success(lang === "nl" ? "Commissie bijgewerkt" : "Commission updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800", suspended: "bg-zinc-200 text-zinc-700" };

  return (
    <div data-testid="admin-seller-detail-page">
      <Link to="/admin/sellers" className="font-label text-ink-muted hover:text-ink flex items-center gap-1.5 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> {lang === "nl" ? "Verkopers" : "Sellers"}</Link>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-label text-ink-muted">{s.is_official ? "SNAPSHOP OFFICIAL" : "SELLER"}</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{s.company_name}</h1>
          <span className={`inline-flex items-center gap-1 mt-2 px-2 py-1 text-[10px] font-label ${TONE[s.status]}`} data-testid={`current-status-${s.status}`}>{s.status.toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {s.status === "approved" && (
            <a href={`/store/${s.slug}`} target="_blank" rel="noreferrer" className="btn-secondary text-xs"><Globe className="w-3.5 h-3.5" /> {lang === "nl" ? "Bekijk winkel" : "View store"}</a>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-black/10 bg-white p-6">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Bedrijfsgegevens" : "Company details"}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Row k="KvK" v={s.kvk_number} />
              <Row k="BTW" v={s.btw_number} />
              <Row k="IBAN" v={s.iban} />
              <Row k={lang === "nl" ? "Land" : "Country"} v={s.country} />
              <Row k={lang === "nl" ? "Adres" : "Address"} v={`${s.street} ${s.house_number}, ${s.postal_code} ${s.city}`} full />
            </div>
          </div>
          <div className="border border-black/10 bg-white p-6">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Contactpersoon" : "Contact"}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Row k={lang === "nl" ? "Naam" : "Name"} v={s.contact_person} />
              <Row k="Email" v={s.contact_email} />
              <Row k={lang === "nl" ? "Telefoon" : "Phone"} v={s.contact_phone} />
              <Row k="Website" v={s.website_url || "—"} />
            </div>
          </div>
          {(s.bio_en || s.bio_nl) && (
            <div className="border border-black/10 bg-white p-6">
              <div className="font-label text-ink-muted mb-3">Bio</div>
              <div className="text-sm space-y-3"><p><strong>NL:</strong> {s.bio_nl || "—"}</p><p><strong>EN:</strong> {s.bio_en || "—"}</p></div>
            </div>
          )}
          {(s.documents || []).length > 0 && (
            <div className="border border-black/10 bg-white p-6">
              <div className="font-label text-ink-muted mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> {lang === "nl" ? "Documenten" : "Documents"}</div>
              <ul className="text-sm space-y-2">
                {(s.documents || []).map((d) => (
                  <li key={d.id} className="flex items-center justify-between border-b border-black/5 pb-2 last:border-0">
                    <span>{d.type} · {d.filename} · {Math.round((d.size || 0) / 1024)}KB</span>
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-terracotta underline text-xs">{lang === "nl" ? "Open" : "Open"}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted">{lang === "nl" ? "Beoordeling" : "Review"}</div>
            <textarea
              data-testid="review-reason"
              rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={lang === "nl" ? "Reden (verplicht bij afwijzen/schorsen)…" : "Reason (required for reject/suspend)…"}
              className="border border-black/15 px-3 py-2 text-sm w-full bg-white"
            />
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="action-approve" disabled={busy || s.status === "approved"} onClick={() => updateStatus("approved")} className="text-sm py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {lang === "nl" ? "Goedkeuren" : "Approve"}</button>
              <button data-testid="action-reject" disabled={busy || s.status === "rejected"} onClick={() => updateStatus("rejected")} className="text-sm py-2 px-3 bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center justify-center gap-1.5"><XCircle className="w-4 h-4" /> {lang === "nl" ? "Afwijzen" : "Reject"}</button>
              <button data-testid="action-suspend" disabled={busy || s.status === "suspended"} onClick={() => updateStatus("suspended")} className="col-span-2 text-sm py-2 px-3 bg-zinc-700 hover:bg-zinc-800 text-white inline-flex items-center justify-center gap-1.5"><Ban className="w-4 h-4" /> {lang === "nl" ? "Schorsen" : "Suspend"}</button>
            </div>
          </div>

          <div className="border border-black/10 bg-white p-6 space-y-3">
            <div className="font-label text-ink-muted flex items-center gap-2"><Percent className="w-4 h-4" /> {lang === "nl" ? "Commissie" : "Commission"}</div>
            <input data-testid="commission-input" type="number" step="0.1" value={commission} onChange={(e) => setCommission(e.target.value)} placeholder={`${15} (default)`} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
            <p className="text-[11px] text-ink-muted">{lang === "nl" ? "Laat leeg om standaard tarief uit instellingen te gebruiken." : "Leave empty to use default from settings."}</p>
            <button data-testid="save-commission" onClick={saveCommission} className="btn-secondary text-xs w-full">{lang === "nl" ? "Opslaan" : "Save"}</button>
          </div>

          <div className="border border-black/10 bg-white p-6 space-y-2 text-sm">
            <div className="font-label text-ink-muted mb-2">{lang === "nl" ? "Statistieken" : "Stats"}</div>
            <Row k={lang === "nl" ? "Producten" : "Products"} v={s.products_count ?? 0} inline />
            <Row k={lang === "nl" ? "Bruto verkoop" : "Gross sales"} v={`€${(s.totals?.sales_gross || 0).toFixed(2)}`} inline />
            <Row k={lang === "nl" ? "Commissie" : "Commission"} v={`€${(s.totals?.commission || 0).toFixed(2)}`} inline />
            <Row k={lang === "nl" ? "Uitbetaald" : "Paid out"} v={`€${(s.totals?.payouts_paid || 0).toFixed(2)}`} inline />
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v, full, inline }) => (
  <div className={`${full ? "sm:col-span-2" : ""} ${inline ? "flex justify-between" : ""}`}>
    <div className="text-xs font-label text-ink-muted">{k}</div>
    <div className={inline ? "" : ""}>{v ?? "—"}</div>
  </div>
);

export default AdminSellerDetail;
